import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

interface Props { step: any; presets: any[]; onClose: () => void; onDone: () => void }
const norm = (s: string) => (s || "").trim().toLowerCase();

export function AddStepToPresetsDialog({ step, presets, onClose, onDone }: Props) {
  const has = (p: any) => (p.items || []).some((t: string) => norm(t) === norm(step.text));
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const toggle = (id: string) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const apply = async () => {
    setBusy(true);
    try {
      await Promise.all(presets.filter((p) => sel.has(p.id)).map((p) => api.put(`/wo-checklist-presets/${p.id}`, { name: p.name, type: p.type, items: [...(p.items || []), step.text] })));
      toast.success(`Step added to ${sel.size} checklist${sel.size === 1 ? "" : "s"}`); onDone(); onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="add-step-dialog">
        <DialogHeader><DialogTitle>Add step to checklists</DialogTitle></DialogHeader>
        <div className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-800" data-testid="add-step-text">“{step.text}”</div>
        <div className="space-y-1.5" data-testid="add-step-preset-list">
          {presets.map((p) => { const used = has(p); return (
            <label key={p.id} className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm ${used ? "border-gray-100 bg-gray-50 text-gray-400" : "border-gray-200 hover:bg-gray-50"}`}>
              <input type="checkbox" data-testid={`add-step-preset-${p.id}`} disabled={used} checked={sel.has(p.id)} onChange={() => toggle(p.id)} />
              <span className="flex-1">{p.name} <span className="text-xs text-gray-400">· {(p.items || []).length} steps</span></span>
              {used && <span className="text-[11px]">already included</span>}
            </label>); })}
          {presets.length === 0 && <div className="text-sm text-gray-500">No checklist presets yet.</div>}
        </div>
        <div className="flex justify-end gap-2"><Button variant="outline" size="sm" onClick={onClose} disabled={busy}>Cancel</Button><Button size="sm" className="bg-blue-700 hover:bg-blue-800" data-testid="add-step-confirm" disabled={busy || sel.size === 0} onClick={apply}>{busy ? "Adding…" : `Add to ${sel.size || ""} checklist${sel.size === 1 ? "" : "s"}`}</Button></div>
      </DialogContent>
    </Dialog>
  );
}
